import { CallLogsResponse, LogsQueryParams, TextLogsResponse } from '@/types/TCRMLogs';
import {
	TApiResponse,
	TCreateFilterViewRequest,
	TCreateFilterViewResponse,
	TCustomer,
	TDeleteFilterViewResponse,
	TGetFilterViewsResponse,
	TSmartVariableDefinition,
} from '@/types/TSmartCRM';
import apiClient from '@/utils/apiClient';

const SMART_CRM_URI = '/api/crm2/variable';
const LEADS_URI = '/api/crm2/lead';
const FILTER_VIEWS_URI = '/api/crm2/filter-views';
const CRM_LOGS_URI = '/api/crm2/leads/logs';

export const getSmartVariables = async (
	isNoviUser: boolean,
	variableId?: string,
): Promise<{
	success: boolean;
	data: {
		variables?: TSmartVariableDefinition[];
		variable?: TSmartVariableDefinition;
		count?: number;
	};
}> => {
	const queryParams = variableId ? `?id=${variableId}` : '';
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${SMART_CRM_URI}${queryParams}`,
		clientType: isNoviUser ? 'NOVI' : 'RECEPTIONIST',
	});

	return response;
};

export const createSmartVariable = async (payload: {
	agentId: string;
	internalKey: string;
	fieldName: string;
	fieldType: string;
	options?: string[];
	description?: string;
	isRequired?: boolean;
}): Promise<{
	success: boolean;
	data: {
		newVariable: TSmartVariableDefinition;
	};
}> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: SMART_CRM_URI,
		clientType: 'NOVI',
		body: payload,
	});

	return response;
};

export const updateSmartVariable = async (payload: {
	variableId: string;
	fieldName?: string;
	fieldType?: string;
	options?: string[];
	description?: string;
	isRequired?: boolean;
}): Promise<{
	success: boolean;
	data: {
		updatedVariable: TSmartVariableDefinition;
	};
}> => {
	const { variableId, ...updateData } = payload;
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${SMART_CRM_URI}?id=${variableId}`,
		clientType: 'NOVI',
		body: updateData,
	});

	return response;
};

export const deleteSmartVariable = async (
	variableId: string,
): Promise<{
	success: boolean;
	message: string;
}> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: `${SMART_CRM_URI}?id=${variableId}`,
		clientType: 'NOVI',
	});

	return response;
};

export const getLeads = async (params?: {
	agentId?: string;
	leadId?: string;
	includeVariables?: string;
	filterGroups?: string;
	variableId?: string;
	pagination?: {
		limit?: number;
		lastEvaluatedKey?: string;
	};
	sortBy?: string;
	sortOrder?: string;
}): Promise<
	TApiResponse<{
		leads?: TCustomer[];
		lead?: TCustomer;
		count?: number;
		groups?: Record<string, TCustomer[]>;
		groupMetadata?: Record<
			string,
			{
				name: string;
				id: string;
			}
		>;
		pagination?: {
			hasMore?: boolean;
			lastEvaluatedKey?: string;
		};
	}>
> => {
	const queryParams = new URLSearchParams();

	if (params?.leadId) {
		queryParams.append('id', params.leadId);
	}
	if (params?.agentId) {
		queryParams.append('agentId', params.agentId);
	}
	if (params?.includeVariables) {
		queryParams.append('includeVariables', params.includeVariables);
	}
	if (params?.filterGroups) {
		queryParams.append('filterGroups', params.filterGroups);
	}
	if (params?.variableId) {
		queryParams.append('variableId', params.variableId);
	}
	if (params?.pagination?.limit) {
		queryParams.append('limit', params.pagination.limit.toString());
	}
	if (params?.pagination?.lastEvaluatedKey) {
		queryParams.append('lastEvaluatedKey', params.pagination.lastEvaluatedKey);
	}
	if (params?.sortBy) {
		queryParams.append('sortBy', params.sortBy);
	}
	if (params?.sortOrder) {
		queryParams.append('sortOrder', params.sortOrder);
	}

	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${LEADS_URI}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
	});

	return response;
};

export const createLead = async (leadData: {
	agentId: string;
	name: string;
	email: string;
	number: string;
	variables?: { [key: string]: any };
}): Promise<
	TApiResponse<{
		newLead: TCustomer;
	}>
> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: LEADS_URI,
		body: leadData,
	});

	return response;
};

export const updateLead = async (
	leadId: string,
	leadData: {
		agentId: string;
		name?: string;
		email?: string;
	},
): Promise<
	TApiResponse<{
		updatedLead: TCustomer;
	}>
> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${LEADS_URI}?id=${leadId}`,
		body: leadData,
	});

	return response;
};

export const deleteLead = async (
	leadId: string,
	variableId?: string,
): Promise<TApiResponse<null>> => {
	let endPoint = `${LEADS_URI}?id=${leadId}`;
	if (variableId) {
		endPoint += `&variableId=${variableId}`;
	}

	const response = await apiClient.request({
		method: 'DELETE',
		endPoint,
	});

	return response;
};

export const createFilterView = async (
	filterViewData: TCreateFilterViewRequest,
	isNoviUser: boolean = false,
): Promise<TCreateFilterViewResponse> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: FILTER_VIEWS_URI,
		body: filterViewData,
		clientType: isNoviUser ? 'NOVI' : 'RECEPTIONIST',
	});
	return response;
};

export const getFilterViews = async (
	agentId: string,
	isNoviUser: boolean = false,
): Promise<TGetFilterViewsResponse> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${FILTER_VIEWS_URI}?agentId=${agentId}`,
		clientType: isNoviUser ? 'NOVI' : 'RECEPTIONIST',
	});
	return response;
};

export const deleteFilterView = async (
	agentId: string,
	viewId: string,
	isNoviUser: boolean = false,
): Promise<TDeleteFilterViewResponse> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: `${FILTER_VIEWS_URI}?agentId=${agentId}&viewId=${viewId}`,
		clientType: isNoviUser ? 'NOVI' : 'RECEPTIONIST',
	});
	return response;
};

export const createBulkLeads = async (bulkData: {
	agentId: string;
	leads: Array<{
		name: string;
		email: string;
		number: string;
		variables?: { [key: string]: any };
	}>;
}): Promise<
	TApiResponse<{
		createdLeads: TCustomer[];
		failedLeads?: Array<{
			index: number;
			error: string;
			data: any;
		}>;
	}>
> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: `${LEADS_URI}/bulk`,
		body: bulkData,
	});

	return response;
};

export const getCRMCallLogs = async (
	params: LogsQueryParams,
	isNoviUser: boolean = false,
): Promise<CallLogsResponse> => {
	const { clientID, leadPhoneNumber, businessPhoneNumber, limit = 5, lastKey } = params;

	let endPoint = `${CRM_LOGS_URI}/call?clientID=${clientID}&leadPhoneNumber=${encodeURIComponent(leadPhoneNumber)}&businessPhoneNumber=${encodeURIComponent(businessPhoneNumber)}&limit=${limit}`;

	if (lastKey) {
		endPoint += `&lastKey=${encodeURIComponent(lastKey)}`;
	}

	const response = await apiClient.request({
		method: 'GET',
		endPoint,
		clientType: isNoviUser ? 'NOVI' : 'RECEPTIONIST',
	});

	return response;
};

export const getCRMTextLogs = async (
	params: LogsQueryParams,
	isNoviUser: boolean = false,
): Promise<TextLogsResponse> => {
	const { clientID, leadPhoneNumber, limit = 20, lastKey } = params;

	let endPoint = `${CRM_LOGS_URI}/text?clientID=${clientID}&phoneNumber=${encodeURIComponent(leadPhoneNumber)}&limit=${limit}`;

	if (lastKey) {
		endPoint += `&lastKey=${encodeURIComponent(lastKey)}`;
	}

	const response = await apiClient.request({
		method: 'GET',
		endPoint,
		clientType: isNoviUser ? 'NOVI' : 'RECEPTIONIST',
	});

	return response;
};

export const getLead = async (leadId: string, agentId: string): Promise<any> => {
	const endPoint = `${LEADS_URI}?id=${leadId}&agentId=${agentId}`;

	const response = await apiClient.request({
		method: 'GET',
		endPoint,
	});

	return response;
};

export const addLeadNote = async (leadId: string, agentId: string, noteData: any): Promise<any> => {
	const endPoint = `${LEADS_URI}?id=${leadId}`;

	const response = await apiClient.request({
		method: 'PATCH',
		endPoint,
		body: {
			agentId,
			...noteData,
		},
	});

	return response;
};
